import { AIContext } from "@/core/battle/ai/context/AIContext";
import { AttackEvaluator } from "@/core/battle/ai/evaluators/AttackEvaluator";
import { MovementEvaluator } from "@/core/battle/ai/evaluators/MovementEvaluator";
import { AttackActionGenerator } from "@/core/battle/ai/generators/AttackActionGenerator";
import { MoveActionGenerator } from "@/core/battle/ai/generators/MoveActionGenerator";
import { WaitActionGenerator } from "@/core/battle/ai/generators/WaitActionGenerator";

import { PruningStrategy } from "@/core/battle/ai/planning/PruningStrategy";
import { SequencePlanner } from "@/core/battle/ai/planning/SequencePlanner";
import { StateSimulator } from "@/core/battle/ai/planning/StateSimulator";
// import { AIStrategicManager } from "@/core/battle/ai/strategic/AIStrategicManager";
import { AIStrategicManager } from "@/core/battle/ai/strategic/AIStrategicManager";

import type { 
    ActionComponent, 
    AIPlan, 
    PlanningContext, 
    PlanningState, 
    StrategicStance,
    ActionCandidate,
    AIActionType 
} from "@/core/battle/ai/types/ai.types";
import  { 
    AIDecisionLogger,
    type DecisionContext, 
    type EvaluatedActionLog 
} from "@/core/battle/ai/AIDecisionLogger";
import type { PlanningResult } from "@/core/battle/ai/planning/PlanningResult";
import { STANCE_THRESHOLDS, PLANNING_CONFIG } from "@/core/battle/ai/utils/AIConfig";
import { FactionType, type BattleUnit } from "@/core/types/battle.types";
import { BattleStore, useBattleStore } from "@/store/useBattleStore";
import { WaitEvaluator } from "@/core/battle/ai/evaluators/WaitEvaluator";
import { EvaluatorUtils } from "@/core/battle/ai/utils/evaluatorUtils";
import { TerminalEvaluator } from "@/core/battle/ai/evaluators/TerminalEvaluator";
import { Goal } from "@/core/battle/ai/strategic/AIGoals";

export class AISystem {
    private currentContext: PlanningContext | null = null;
    
    private readonly generators = {
        attack: new AttackActionGenerator(),
        move: new MoveActionGenerator(),
        wait: new WaitActionGenerator()
    };

    constructor(
        private strategicManager: AIStrategicManager,
        private sequencePlanner: SequencePlanner,
        private decisionLogger: AIDecisionLogger
    ) {}

    static create(strategicManager: AIStrategicManager): AISystem {
        // Create planning components
        const pruningStrategy = new PruningStrategy();
        const stateSimulator = new StateSimulator();
        const terminalEvaluator = new TerminalEvaluator();
        
        const sequencePlanner = new SequencePlanner(
            stateSimulator,
            pruningStrategy,
            terminalEvaluator
        );
        
        // Create decision logger (enabled by PLANNING_CONFIG.debug)
        const decisionLogger = new AIDecisionLogger(PLANNING_CONFIG.debug);
        
        return new AISystem(strategicManager, sequencePlanner, decisionLogger);
    }

    public getNextAction(unitId: string): AIPlan | null {
        const state = useBattleStore.getState();
        const unit = state.units.get(unitId);
        if (!unit) return null;

        const enemies: BattleUnit[] = [];
        const allies: BattleUnit[] = [];
        for (const u of state.units.values()) {
            if (u.isDead) continue;
            if (u.faction === unit.faction) allies.push(u);
            else enemies.push(u);
        }

        
        const isNewRound = this.strategicManager.getCurrentTurn() !== state.round;
        if (isNewRound) {
            this.strategicManager.onTurnStart(allies, enemies, state.battleMap, state.round);
            this.strategicManager.assignGoalsToAll(allies, enemies);
            this.currentContext = null;
        }

        const goal = this.strategicManager.getGoalForUnit(unit, allies, enemies, state.battleMap);
        const ctx = this.getOrCreateContext(unit, allies, enemies, goal);
        ctx.goal = goal;

        // 3. Get components for this unit
        const components = this.getComponentsForUnit(unit, state);

        const initialState: PlanningState = {
            position: { q: unit.q, r: unit.r },
            stamina: unit.currentStamina,
            actionsUsed: [],
            totalUtility: 0,
            depth: 0,
        };

        // 4. Plan action (returns PlanningResult with metadata)
        const result: PlanningResult = this.sequencePlanner.planBestSequence(
            initialState, 
            ctx, 
            components, 
            5
        );
        
        // 5. Log decision (if debug enabled)
        if (PLANNING_CONFIG.debug) {
            this.logDecision(unit, ctx, result);
        }
        
        
        // 6. Return first action or wait
        if (!result.sequence || result.sequence.actions.length === 0) {
            return { type: 'wait', unitId: unit.unitId, utility: 0 };
        }

        return result.sequence.actions[0]!;
    }

    
    
    
    /**
     * Convert ActionCandidate to EvaluatedActionLog
     * Builds human-readable description
     */
    private candidateToLogEntry(
        candidate: ActionCandidate,
        ctx: PlanningContext
    ): EvaluatedActionLog {
        const action = candidate.action;
        
        // Build description and calculate stamina cost
        let description = '';
        let staminaCost = 0;
        
        switch (action.type) {
            case 'attack': {
                const target = ctx.enemies.find(e => e.unitId === action.targetUnitId);
                const skill = ctx.unit.skills.find(s => s.id === action.skillId);
                description = `Attack ${target?.unitId || 'unknown'} with ${skill?.name || 'skill'}`;
                staminaCost = skill?.cost || 0;
                break;
            }
                
            case 'move': {
                const moveType = candidate.metadata?.type || 'move';
                description = `Move to ${action.targetHexId} (${moveType})`;
                staminaCost = ctx.unit.currentStamina - candidate.staminaAfter;
                break;
            }
                
            case 'wait': {
                description = 'Wait';
                staminaCost = 0;
                break;
            }
                
            default: {
                description = `Unknown action: ${action.type}`;
                staminaCost = 0;
            }
        }
        
        return {
            type: action.type as 'attack' | 'move' | 'wait',
            description,
            position: action.type === 'move' ? action.targetHexId : undefined,
            targetId: action.type === 'attack' ? action.targetUnitId : undefined,
            skillId: action.type === 'attack' ? action.skillId : undefined,
            staminaCost,
            staminaAfter: candidate.staminaAfter,
            utility: candidate.utility,
            components: candidate.components || { total: candidate.utility }
        };
    }

    private getOrCreateContext(unit: BattleUnit, allies: BattleUnit[], enemies: BattleUnit[], goal: Goal | null): PlanningContext {
        if (this.currentContext) {
            const isSameUnit = this.currentContext.unit.unitId === unit.unitId;
            const isSameComposition = 
                this.currentContext.allies.length === allies.length && 
                this.currentContext.enemies.length === enemies.length;
            const isSameGoal = this.currentContext.goal?.id === goal?.id;

            if (isSameUnit && isSameComposition && isSameGoal) {
                return this.currentContext;
            }
        }

        const stance = this.determineStance();
        const state = useBattleStore.getState();
        this.currentContext = AIContext.create(unit, state.units, state.battleMap, stance, goal, state.round);
        console.log(`[AIPlanner] Recreating context for ${unit.unitId}. `, this.currentContext);
        
        return this.currentContext;
    }

    // private getOrCreateContext(unit: BattleUnit, allies: BattleUnit[], enemies: BattleUnit[]): PlanningContext {
    //     const state = useBattleStore.getState();
        
    //     // 1. Всегда получаем цель от StrategicManager
    //     const newGoal = this.strategicManager.getGoalForUnit(unit, allies, enemies, state.battleMap);

    //     // // 2. Проверяем, нужно ли пересоздавать контекст
    //     if (this.currentContext) {
    //         const isSameUnit = this.currentContext.unit.unitId === unit.unitId;
    //         const isSameComposition = 
    //             this.currentContext.allies.length === allies.length && 
    //             this.currentContext.enemies.length === enemies.length;
            
    //         // Сравниваем ID целей. Если цели нет (null), проверяем оба на null
    //         const isSameGoal = this.currentContext.goal?.id === newGoal?.id;

    //         if (isSameUnit && isSameComposition && isSameGoal) {
    //             console.log("currentContext", unit)
    //             return this.currentContext;
    //         }
    //     }

    //     // 3. Если условия не выполнены — полная перегенерация
    //     console.log(`[AIPlanner] Recreating context for ${unit.unitId}. Reason: composition or goal change.`);
        
    //     const stance = this.determineStance();
    //     // Передаем allies и enemies, чтобы контекст сохранил их длину для следующей проверки

    //     this.currentContext = AIContext.create(
    //         unit, 
    //         state.units, 
    //         state.battleMap, 
    //         stance, 
    //         newGoal,
    //         state.round,
    //     );
        
    //     console.log(`[AIPlanner] Recreating context for ${unit.unitId}. `, this.currentContext);
    //     return this.currentContext;
    // }

    private getComponentsForUnit(unit: BattleUnit, state: BattleStore): ActionComponent[] {
        const components: ActionComponent[] = [];

        components.push({
            generator: this.generators.move,
            evaluator: new MovementEvaluator() 
        });

        components.push({
            generator: this.generators.attack,
            evaluator: new AttackEvaluator() 
        });

        components.push({
            generator: this.generators.wait,
            evaluator: new MovementEvaluator() 
        });

        return components;
    }

    private determineStance(): StrategicStance {
        const units = Array.from(useBattleStore.getState().units.values()).filter(u => !u.isDead);

        const enemyPower = units
            .filter(u => u.faction === FactionType.Enemy)
            .reduce((sum, u) => sum + EvaluatorUtils.calculateStackPower(u), 0);

        const playerPower = units
            .filter(u => u.faction === FactionType.Player)
            .reduce((sum, u) => sum + EvaluatorUtils.calculateStackPower(u), 0);

        const ratio = (enemyPower/100) / ((playerPower || 1)/100);

        if (ratio > STANCE_THRESHOLDS.AGGRESSIVE_RATIO) return 'AGGRESSIVE';
        if (ratio < 0.25) return 'AGGRESSIVE';
        if (ratio < STANCE_THRESHOLDS.DEFENSIVE_RATIO) return 'DEFENSIVE';
        
        return 'BALANCED';
    }

    /**
     * Log AI decision for debugging
     * Converts candidates to logger format
     */
    private logDecision(
        unit: BattleUnit,
        ctx: PlanningContext,
        result: PlanningResult
    ): void {
        const context: DecisionContext = {
            unitId: unit.unitId,
            unitName: unit.unitId.split('_')[0] || '-',
            stance: ctx.stance,
            position: `${unit.q}:${unit.r}`,
            currentHp: `${unit.currentUnitHp}/${unit.stats.hp}`,
            currentStamina: unit.currentStamina
        };
        
        // Convert top candidates for display
        const topCandidates: EvaluatedActionLog[] = result.metadata.topCandidates
            .slice(0, 5)
            .map(candidate => this.candidateToLogEntry(candidate, ctx));
        
        // ✅ FIX: Get final decision from actual sequence, not topCandidates
        let finalDecision: EvaluatedActionLog | null = null;
        
        if (result.sequence && result.sequence.actions.length > 0) {
            // Find the candidate that matches the actual chosen action
            const chosenAction = result.sequence.actions[0];
            const chosenCandidate = result.metadata.topCandidates.find(c => 
                this.actionsMatch(c.action, chosenAction!)
            );
            
            if (chosenCandidate) {
                finalDecision = this.candidateToLogEntry(chosenCandidate, ctx);
            } else {
                // Fallback: create log entry from action
                finalDecision = this.actionToLogEntry(chosenAction!, ctx, result.sequence.totalUtility);
            }
        }
        
        // Log
        this.decisionLogger.logDecision({
            context,
            candidatesCount: result.metadata.candidatesGenerated,
            topCandidates,
            finalDecision,
            planDepth: result.sequence?.actions.length || 0,
            timestamp: Date.now()
        });
    }

    /**
     * Check if two actions match
     */
    private actionsMatch(a: AIPlan, b: AIPlan): boolean {
        if (a.type !== b.type) return false;
        
        switch (a.type) {
            case 'attack':
                return a.targetUnitId === b.targetUnitId && a.skillId === b.skillId;
            case 'move':
                return a.targetHexId === b.targetHexId;
            case 'wait':
                return true;
            default:
                return false;
        }
    }

    /**
     * Convert AIPlan to EvaluatedActionLog (when candidate not found)
     */
    private actionToLogEntry(
        action: AIPlan,
        ctx: PlanningContext,
        totalUtility: number
    ): EvaluatedActionLog {
        let description = '';
        let staminaCost = 0;
        
        switch (action.type) {
            case 'attack': {
                const target = ctx.enemies.find(e => e.unitId === action.targetUnitId);
                const skill = ctx.unit.skills.find(s => s.id === action.skillId);
                description = `Attack ${target?.unitId || 'unknown'} with ${skill?.name || 'skill'}`;
                staminaCost = skill?.cost || 0;
                break;
            }
            case 'move': {
                description = `Move to ${action.targetHexId}`;
                staminaCost = ctx.unit.currentStamina - (ctx.reachableMap.get(action.targetHexId!) || 0);
                break;
            }
            case 'wait': {
                description = 'Wait';
                staminaCost = 0;
                break;
            }
        }
        
        return {
            type: action.type as 'attack' | 'move' | 'wait',
            description,
            position: action.type === 'move' ? action.targetHexId : undefined,
            targetId: action.type === 'attack' ? action.targetUnitId : undefined,
            skillId: action.type === 'attack' ? action.skillId : undefined,
            staminaCost,
            staminaAfter: ctx.unit.currentStamina - staminaCost,
            utility: totalUtility,
            components: { total: totalUtility }
        };
    }
}